import { Button } from 'flowbite-react';
import moment from 'moment';
import React, { useState } from 'react';
import { browserName } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import { findRemainingAttempts, isUserCollegeUser } from 'shared-utils';
import { useThemeContext } from 'src/context/ThemeProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetAllSellableRoundTemplates } from 'src/hooks/roundTemplate.hooks';
import { sendTrackingEvent } from 'src/utils/gtm';
import { DayTimeSlot, MeetingStatus, SlotTime } from 'types';
import { Loader } from 'ui';

type Props = {
	selectedTeckStack: string;
	selectedRound: string;
	selectedDaySlot?: DayTimeSlot;
	selectedTimeSlot?: SlotTime;
	isLoading?: boolean;
	hasAgreed: boolean;
	hasReschedulableMeeting: boolean;
	isAIRoundSelected: boolean;
	setCurrentStep: (val: number) => void;
	onClose: () => void;
	onSchedule: () => void;
	scheduleForLater?: boolean;
	onScheduleForLater: () => void;
	showMockTestOnly?: boolean;
	disabled?: boolean;
};

const DesktopPaymentButtons = ({
	selectedTeckStack,
	selectedRound,
	selectedDaySlot,
	selectedTimeSlot,
	isLoading,
	hasAgreed,
	hasReschedulableMeeting,
	setCurrentStep,
	onSchedule,
	onClose,
	isAIRoundSelected,
	scheduleForLater,
	onScheduleForLater,
	showMockTestOnly,
	disabled,
}: Props) => {
	const location = useLocation();
	const { themeMode } = useThemeContext();
	const { showStartAIInterviewNow, userRounds, isJobseekerLoggedIn, user } = useUserContext();

	const totalMockAttempts = userRounds
		?.filter(r => r?.isMock)
		?.reduce((acc, r) => {
			return acc + r.maxAttempts;
		}, 0);
	const totalMockAttemptsDone = userRounds
		?.filter(r => r?.isMock)
		?.reduce((acc, r) => {
			return acc + (r.maxAttempts - findRemainingAttempts(r.maxAttempts, r.meetings));
		}, 0);

	const { data: roundTemplateData } = useGetAllSellableRoundTemplates(isJobseekerLoggedIn, totalMockAttempts === 0);
	const totalMockSellableRoundTemplates = roundTemplateData?.roundTemplates?.filter(el => el?.isMock)?.length;

	return (
		<div className="flex flex-col gap-3 items-start md:pb-[37px]">
			{showMockTestOnly ? (
				<div className="text-[#8F90A6] italic text-sm mb-[10px] font-[500]">
					Note: These mock interviews are purely for practice and will not impact your overall score.
				</div>
			) : null}

			<div className="flex flex-row gap-3 items-start mt-auto w-full">
				{!isAIRoundSelected || scheduleForLater ? (
					<>
						<Button
							color="purplePlaced"
							className="bg-[#7777E7] px-[20px] text-white"
							disabled={
								(!selectedTeckStack && !selectedRound) ||
								(!isAIRoundSelected && !selectedDaySlot) ||
								!selectedTimeSlot ||
								isLoading ||
								(!isAIRoundSelected && !hasAgreed)
							}
							onClick={() => {
								if (hasReschedulableMeeting) {
									sendTrackingEvent({
										event: 'interview_reschedule',
										eventLabel: location.pathname,
									});
								} else {
									sendTrackingEvent({
										event: 'interview_schedule',
										eventLabel: location.pathname,
									});
								}

								setCurrentStep(1);
								onSchedule();
								// onClose();
							}}
						>
							{isLoading ? (
								<div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
									<Loader />
								</div>
							) : null}
							<div>
								{hasReschedulableMeeting ? 'Reschedule' : 'Schedule'} for{' '}
								{!isAIRoundSelected
									? `${moment(selectedDaySlot?.date).format('DD MMM YYYY')} at ${
											selectedTimeSlot?.time
												? moment(selectedTimeSlot?.time)
														.format('hh:mm a')
														.toUpperCase()
												: '____'
									  }`
									: null}
								{isAIRoundSelected && selectedTimeSlot
									? `${moment(selectedTimeSlot?.time).format('DD MMM YYYY')} at ${moment(selectedTimeSlot?.time)
											.format('hh:mm a')
											.toUpperCase()}`
									: '____'}
							</div>
						</Button>
						<Button
							color={themeMode === 'light' ? 'whiteBlueOutline' : 'darkBlue'}
							disabled={isLoading}
							onClick={() => {
								sendTrackingEvent({
									event: 'interview_drawer_cancel',
									eventLabel: location.pathname,
								});

								setCurrentStep(1);
								onClose();
							}}
						>
							Cancel
						</Button>
					</>
				) : (
					<div className="flex flex-row items-center flex-wrap w-full">
						{showStartAIInterviewNow ? (
							<Button
								color="purplePlaced"
								className="w-full max-w-[190px] bg-[#7777E7]"
								disabled={
									(!selectedTeckStack && !selectedRound) ||
									isLoading ||
									(showMockTestOnly && totalMockAttempts === 0 && totalMockSellableRoundTemplates === 0) ||
									disabled
								}
								onClick={() => {
									onSchedule();
								}}
							>
								{isLoading ? (
									<div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
										<Loader />
									</div>
								) : null}
								Start Interview
							</Button>
						) : null}

						{showStartAIInterviewNow && user && !isUserCollegeUser(user) ? (
							<Button
								disabled={disabled}
								color={themeMode === 'light' ? 'whiteBlueOutline' : 'darkBlue'}
								onClick={onScheduleForLater}
								className="ml-2"
							>
								Schedule for Later
							</Button>
						) : null}

						{showMockTestOnly && totalMockAttempts ? (
							<div className="text-[#0063F7] text-[12px] font-[500] ml-[15px]">
								Attempts: {totalMockAttemptsDone}/{totalMockAttempts}
							</div>
						) : null}
					</div>
				)}
			</div>
		</div>
	);
};

export default DesktopPaymentButtons;
